import { SkycFormWidgetPropTypes } from '@airtel/air-widgets-library';
import faviconIcon from '../assets/images/ico/favicon.ico';
import {
   getInteractionId,
   generateUniqueId,
   getCallUtilFunction,
   getNativeEventEmitterAction,
   getStoreValuesAction,
   prepareDisabilityValidFormMetaData,
} from './commonUtils';
import { ImageStore } from './userImageUploadUtils';
import { getDisabilityCtaAction, getDisabilityCtaState, prepareRevampDisabilityValidFormMetaData } from './commonUtilsRevamp';
import { PWD_BACK_IMAGE, PWD_FRONT_IMAGE } from '../constants/AppConstants';

export async function prepareFormData(
   data: { [key: string]: any },
   skipFileTransformation = false,
   imageKey: string = 'imageValue'
): Promise<FormData> {
   let rawImageValue;
   const rawImageFormData = new FormData();

   // Iterate through the data object and append each key-value pair to the FormData
   for (const key in data) {
      if (key !== 'imageValue') {
         rawImageFormData.append(key, data[key]);
      }
   }

   // Append the rawImageValue separately
   if (skipFileTransformation) {
      rawImageValue = data?.[imageKey];
   } else {
      rawImageValue = await (await fetch(data?.[imageKey])).blob();
   }
   rawImageFormData.append(imageKey, rawImageValue, `${generateUniqueId()}.jpeg`);

   return rawImageFormData;
}

export async function prepareRevampFormData(
   data: any,
   skipFileTransformation = false,
   imageKey = 'imageValue'
): Promise<FormData> {
   const formData = new FormData();
   Object.keys(data).forEach((key) => {
      if (key === imageKey || data[key] == null) {
         return;
      }
      formData.append(key, String(data[key]));
   });
   const imageValue = data?.[imageKey];
   if (!imageValue) {
      throw new Error(`Missing upload field: ${imageKey}`);
   }
   const file =
      imageValue instanceof Blob
         ? imageValue
         : skipFileTransformation
           ? imageValue
           : await (await fetch(imageValue)).blob();
   formData.append(
      imageKey,
      file,
      `${generateUniqueId()}.${file?.type === 'application/pdf' ? 'pdf' : 'jpeg'}`
   );
   return formData;
}

export function getUploadAction(
   body: any,
   onSuccessActions: any[],
   onErrorActions: any[],
   headers: any
) {
   let apiUrl = 'new-acq/dochub/doc-hub/upload/image';

   return {
      type: 'apiCall',
      payload: {
         requestType: 'POST',
         apiUrl,
         authTokenGroup: 'SKYC_REVERIFICATION',
         ...{ headers },
         body,
         onSuccess: {
            actions: onSuccessActions,
         },
         onError: onErrorActions,
      },
   };
}

const getUploadLoaderAction = (
   uploadContainerId: string,
   isUploading: boolean,
   errorText = ''
) => {
   const uploadDataKey = `uploadsDataProps.${uploadContainerId}`;
   return {
      [`${uploadDataKey}.errorText`]: errorText,
      [`${uploadDataKey}.isUploading`]: isUploading,
   };
};

export const uploadDisabilityProof = async ({
   additionalData,
   getState,
   onUserAction,
}: any) => {
   try {
      const { storeValues } = getState();
      const latitude = storeValues?.appData?.address?.latitude;
      const longitude = storeValues?.appData?.address?.longitude;
      const { type, file } = additionalData?.dataFromWidget ?? {};
      const widgetId = 'DISABILITY_INFO';
      const journeyType = storeValues?.appData?.lob;

      let headers = {};

      const mappingKeys: any = {
         front: {
            imageType: PWD_FRONT_IMAGE,
            uploadContainerId: 'uploadFrontImageDisability',
            imageUploadedKey: 'isFrontImageUploaded',
         },
         back: {
            imageType: PWD_BACK_IMAGE,
            uploadContainerId: 'uploadBackImageDisability',
            imageUploadedKey: 'isBackImageUploaded',
         },
      };
      const { imageType, uploadContainerId, imageUploadedKey } = mappingKeys[type];
      const circle = storeValues?.appData?.address?.circleId;


         const kycId = storeValues?.appData?.kyc?.kycId;
         const circleId = storeValues?.appData?.address?.circleId;
         const consumerLob = storeValues?.appData?.lob;
         let imageKey = 'docFile';
         headers = {
            source: 'shop',
            'x-correlation-id': generateUniqueId(),
            'x-consumer-lob': consumerLob,
            circleId,
         };
         let documentData = {
            docFile: await getProcessedImage({ getState }, file),
            docType: imageType,
            kycId,
            kycType: 'SKYC',
            channel: 'SHOP',
            circle,
            latitude,
            longitude,
            journeyType,
            requestTime: new Date().toISOString(),
         };


      const body = await prepareFormData(documentData, false, imageKey);

      const startLoaderAction: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, true),
      };

      const showSuccessEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false),
      };

      const showErrorEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false, 'Failed to upload image.'),
      };

      const onError = [
         getNativeEventEmitterAction(widgetId, showErrorEventData),
         getStoreValuesAction('pwdData', {
            [imageUploadedKey]: false,
         }),
      ];

      // **FIX: Only prepare valid form metadata if the entire form is actually valid**
      let validStepData: any[] = [];

      // Check if form is complete before triggering step collapse
      const { disability, dropdownValue, disabilityPercent } =
         additionalData?.dataFromWidget ?? {};
      const { isFrontImageUploaded } = storeValues?.pwdData || {};

      // Important: during front upload success, Redux may not yet have
      // pwdData.isFrontImageUploaded=true. So current front upload counts.
      const willHaveFrontImage = isFrontImageUploaded || type === 'front';

      const isFormComplete =
         disability?.toLowerCase() === 'yes' &&
         !!dropdownValue &&
         !!disabilityPercent &&
         willHaveFrontImage;

      if (isFormComplete) {
         validStepData = prepareDisabilityValidFormMetaData({
            additionalData,
            getState,
            onUserAction,
            collapseDelay: 2000, // 2 s grace period so user can optionally upload back image
         });
      }

      const onSuccessEventData = [
         getNativeEventEmitterAction(widgetId, showSuccessEventData),
         getStoreValuesAction('pwdData', {
            [imageUploadedKey]: true,
         }),
         ...validStepData, // Only added when form is complete
      ];

      const onSuccess = [
         getCallUtilFunction('onSuccessUpload', [
            {
               onSuccess: onSuccessEventData,
               onError,
            },
         ]),
      ];

      onUserAction({
         id: 'uploadDisabilityProof',
         actions: [
            getStoreValuesAction('pwdData', {
               [imageUploadedKey]: false,
            }),
            getNativeEventEmitterAction(widgetId, startLoaderAction),
            getUploadAction(body, onSuccess, onError, headers),
         ],
      });
   } catch (e) {
      console.error('Error during uploading file');
   }
};

type DisabilityUploadSide = 'front' | 'back';

const disabilityUploadConfig = {
   front: {
      widgetId: 'Disability_UploadFront',
      uploadContainerId: 'skyc-front-file-upload',
      imageType: 'PWD_FRONT_IMAGE',
      imageUploadedKey: 'isFrontImageUploaded',
   },
   back: {
      widgetId: 'Disability_UploadBack',
      uploadContainerId: 'skyc-back-file-upload',
      imageType: 'PWD_BACK_IMAGE',
      imageUploadedKey: 'isBackImageUploaded',
   },
} as const;

const REVAMP_PWD_DOC_TYPE : Record<DisabilityUploadSide,string>={
   front:'PWD_FRONT_IMAGE',
   back:'PWD_BACK_IMAGE',
}

const resolveDisabilityUploadSide = (value: unknown): DisabilityUploadSide | null => {
   switch (value) {
      case 'front':
      case 'uploadFrontImageDisability':
      case 'Disability_UploadFront':
      case 'skyc-front-file-upload':
         return 'front';

      case 'back':
      case 'uploadBackImageDisability':
      case 'Disability_UploadBack':
      case 'skyc-back-file-upload':
         return 'back';

      default:
         return null;
   }
};

export const uploadRevampDisabilityProof = async ({
   args,
   additionalData,
   getState,
   onUserAction,
}: any) => {
   let config: (typeof disabilityUploadConfig)[DisabilityUploadSide] | undefined;
   console.log('inside util');
   try {
      const { storeValues } = getState();

      const widgetData = additionalData?.dataFromWidget ?? {};
      const type =
         additionalData?.args?.[0] || additionalData?.action?.payload?.args?.[0];
      const file =
         widgetData?.event?.target?.files?.[0] ??
         widgetData?.event?.nativeEvent?.target?.files?.[0];
      console.log('SKYC upload ', {
         type,
         hasFile: !!file,
         fileName: file?.name,
         fileSize: file?.size,
         widgetDataKeys: Object.keys(widgetData),
      });
      // PageSpace passes the upload identifier in args.
      // Fall back to the widget event payload if necessary.

      const uploadIdentifier =
         args?.[0] ??
         additionalData?.args?.[0] ??
         widgetData?.uploadType ??
         widgetData?.type;

      const side = resolveDisabilityUploadSide(uploadIdentifier);
      const fileName = file?.name;
      const fileNameKey = side === 'front' ? 'frontFileName' : 'backFileName';
      if (!side) {
         console.error('Invalid disability upload identifier', uploadIdentifier);
         return;
      }

      config = disabilityUploadConfig[side];

      // Resolve the selected file from the widget event.

      if (!file) {
         console.error('Disability upload: selected file missing', { side });

         onUserAction?.({
            actions: [
               getNativeEventEmitterAction(config.widgetId, {
                  ...getUploadLoaderAction(
                     config.uploadContainerId,
                     false,
                     'Unable to read selected file.'
                  ),
               }),
            ],
         });

         return;
      }

      const appData = storeValues?.appData ?? {};
      const address = appData?.address ?? {};
      const userLocation = storeValues?.userLocation ?? {};

      const kycId = appData?.kyc?.kycId;
      const circleId = address?.circleId;
      const consumerLob = appData?.lob;

      if (!kycId || !circleId || !consumerLob) {
         console.error('Disability upload: required KYC context missing', {
            hasKycId: Boolean(kycId),
            hasCircleId: Boolean(circleId),
            hasLob: Boolean(consumerLob),
         });
         return;
      }

      const latitude = address?.latitude ?? userLocation?.latitude;

      const longitude = address?.longitude ?? userLocation?.longitude;

      const imageType = REVAMP_PWD_DOC_TYPE[side] ;
      const uploadContainerId = config.uploadContainerId;
      const widgetId = config.widgetId;
      const imageUploadedKey = config.imageUploadedKey;
      console.log(
         'checklist',
         address,
         kycId,
         circleId,
         consumerLob,
         latitude,
         longitude,
         imageType
      );

      const headers = {
         source: 'shop',
         'x-correlation-id': generateUniqueId(),
         'x-consumer-lob': consumerLob,
         circleId,
      };
      console.log('begore image processing', {
         fileType: file?.type,
         fileSize: file?.size,
      });
      const processedImage = await getRevampProcessedImage({ getState }, file);
      console.log('After image processing', {
         hasProcessedImage: !!processedImage,
         processedType: typeof processedImage,
      });
      const documentData: any = {
         docFile: processedImage,
         docType: imageType,
         kycId,
         kycType: 'SKYC',
         channel: 'SHOP',
         circle: circleId,
         journeyType: consumerLob,
         requestTime: new Date().toISOString(),
      };
      console.log('Documentdata', documentData);
      if (latitude != null) {
         documentData.latitude = String(latitude);
      }

      if (longitude != null) {
         documentData.longitude = String(longitude);
      }

      const body = await prepareFormData(documentData, false, 'docFile');

      const startLoaderEventData = {
         ...getUploadLoaderAction(uploadContainerId, true),
      };

      const showSuccessEventData = {
         ...getUploadLoaderAction(uploadContainerId, false),
      };

      const showErrorEventData = {
         ...getUploadLoaderAction(uploadContainerId, false, 'Failed to upload image.'),
      };

      const onError = [getNativeEventEmitterAction(widgetId, showErrorEventData)];

      // On success, update ONLY pwdData.
      // Preserve selected disability and all other fields.
      const onSuccessEventData: any[] = [
         getNativeEventEmitterAction(widgetId, showSuccessEventData),
         getStoreValuesAction('pwdData', {
            [imageUploadedKey]: true,
            [fileNameKey]: fileName,
         }),
         // update the upload widget
         getNativeEventEmitterAction(widgetId, {
            uploadedFiles: [
               {
                  title: fileName,
                  subTitle: 'Uploaded Successfully',
                  state: 'success',
                  showDeleteIcon: true,
                  onDeleteIconPress: {
                     id: `deleteDisability${side === 'front' ? 'Front' : 'Back'}`,
                     actions: [
                        {
                           type: 'callUtilFunction',
                           payload: {
                              utilName: 'deleteRevampDisabilityProof',
                              args: [side],
                           },
                        },
                     ],
                  },
               },
            ],
            hideOnUpload: true,
         }),
         //stopLoader
         getNativeEventEmitterAction(widgetId, {
            ...getUploadLoaderAction(uploadContainerId, false),
         }),
         // Validate from Redux, accounting for this
         // upload succeeding before Redux is refreshed.
         ...prepareRevampDisabilityValidFormMetaData({
            getState,
            uploadedSide: side,
         }),
      ];

      // Keep the existing upload-success callback pipeline.
      const onSuccess = [
         getCallUtilFunction('onSuccessUpload', [
            {
               onSuccess: onSuccessEventData,
               onError,
            },
         ]),
      ];

      const uploadAction = getUploadAction(
         body,
         onSuccessEventData,
         onError,
         headers
      );
      console.log('Dochub upload action prepared', {
         type: uploadAction?.type,
         apiUrl: uploadAction?.payload?.apiUrl,
      });
      onUserAction?.({
         id: `disability-upload-${side}`,
         actions: [
            getNativeEventEmitterAction(widgetId, startLoaderEventData),
            uploadAction,
         ],
      });
   } catch (error) {
      console.error('Error during disability proof upload', error);

      if (config) {
         onUserAction?.({
            actions: [
               getNativeEventEmitterAction(
                  config.widgetId,
                  getUploadLoaderAction(
                     config.uploadContainerId,
                     false,
                     'Failed to upload image.'
                  )
               ),
            ],
         });
      }
   }
};

const getImageType = (type: string, isNewSKYCJourney: boolean) => {
   return isNewSKYCJourney ? type : `${type}_IMAGE`;
};

export const uploadPOAAndPOIProof = async ({
   args,
   additionalData,
   getState,
   onUserAction,
}: any) => {
   try {
      const { storeValues } = getState();
      const latitude = storeValues?.appData?.address?.latitude;
      const longitude = storeValues?.appData?.address?.longitude;
      const { type, file } = additionalData?.dataFromWidget ?? {};
      const imageSameAsPoa = args?.[1];
      const widgetId = 'IDENTITY_PROOF';
      const circle = storeValues?.appData?.address?.circleId;
      const journeyType = storeValues?.appData?.lob;
      const mappingKeys: any = {
         addressFront: {
            imageType: 'REFEREE_POA_FRONT',
            uploadContainerId: 'uploadFrontAddressProof',
            imageUploadedKey: 'isFrontAddressProofUploaded',
         },
         addressBack: {
            imageType: 'REFEREE_POA_BACK',
            uploadContainerId: 'uploadBackAddressProof',
            imageUploadedKey: 'isBackAddressProofUploaded',
         },
         identityFront: {
            imageType: 'REFEREE_POI_FRONT',
            uploadContainerId: 'uploadFrontIdProof',
            imageUploadedKey: 'isFrontIdProofUploaded',
         },
         identityBack: {
            imageType: 'REFEREE_POI_BACK',
            uploadContainerId: 'uploadBackIdProof',
            imageUploadedKey: 'isBackIdProofUploaded',
         },
      };
      const { imageType, uploadContainerId, imageUploadedKey } = mappingKeys[type];


      let headers = {};


         let imageKey = 'docFile';

         const circleId = storeValues?.appData?.address?.circleId;
         const consumerLob = storeValues?.appData?.lob;
         headers = {
            source: 'shop',
            'x-correlation-id': generateUniqueId(),
            'x-consumer-lob': consumerLob,
            circleId,
         };

         let documentData = {
            docFile: await getProcessedImage({ getState }, file),
            docType: imageType,
            interactionId: storeValues?.appData?.kyc?.kycId,
            kycType: 'SKYC',
            channel: 'SHOP',
            circle,
            latitude,
            longitude,
            journeyType,
            requestTime: new Date().toISOString(),
         };


      const body = await prepareFormData(documentData, false, imageKey);

      const startLoaderAction: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, true),
      };

      const showSuccessEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false),
      };

      const showErrorEventData: SkycFormWidgetPropTypes = {
         ...getUploadLoaderAction(uploadContainerId, false, 'Failed to upload image.'),
      };

      const onError = [
         getNativeEventEmitterAction(widgetId, showErrorEventData),
         getStoreValuesAction('identityData', {
            [imageUploadedKey]: false,
         }),
      ];

      const onSuccessEventData = [
         getNativeEventEmitterAction(widgetId, showSuccessEventData),
         getStoreValuesAction('identityData', {
            [imageUploadedKey]: true,
         }),
      ];

      const onSuccess = [
         getCallUtilFunction('onSuccessUpload', [
            {
               onSuccess: onSuccessEventData,
               onError,
            },
         ]),
      ];

      onUserAction({
         id: 'uploadPOAAndPOIProof',
         actions: [
            getStoreValuesAction('identityData', {
               [imageUploadedKey]: false,
            }),
            getNativeEventEmitterAction(widgetId, startLoaderAction),
            getUploadAction(body, onSuccess, onError, headers),
         ],
      });
   } catch (e) {
      console.error('Error during uploading file');
   }
};

export const onSuccessUpload = ({
   args,
   additionalData,
   onUserAction,
   getState,
}: any) => {
   const { storeValues = {} } = getState();
   const journey = storeValues?.journeyContext?.journey ?? storeValues?.appData?.journey;
   const isDocHubJourney = journey === 'SKYC_NEW' || journey === 'SKYC_REVAMP';
   const response = additionalData?.dataFromWidget?.response;
   const status = response?.status;
   const isSuccess = isDocHubJourney
      ? String(status) === '200'
      : String(status?.code) === '200';
   const callbackActions = isSuccess ? args?.[0]?.onSuccess : args?.[0]?.onError;
   console.log('Upload response validation', {
      journey,
      status,
      isSuccess,
      hasCallbackActions: Array.isArray(callbackActions),
   });
   if (Array.isArray(callbackActions)) {
      onUserAction?.({
         id: isSuccess
            ? 'ON_SUCCESS_UPLOAD_UTIL_SUCCESS'
            : 'ON_SUCCESS_UPLOAD_UTIL_ERROR',

         actions: callbackActions,
      });
   }
};

export const savePOIImages = async ({ additionalData }: any) => {
   const { type, file } = additionalData?.dataFromWidget ?? {};

   const imageStore = ImageStore.getInstance();
   imageStore.setItem(type, file);
};

const getProcessedImage = ({ getState }: any, file: any) => {
   return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = function (e) {
         try {
            if (!(typeof e?.target?.result === 'string')) {
               reject();
            } else {
               const imageObj = new Image();

               imageObj.onload = async function () {
                  const canvas = document.createElement('canvas');
                  const context = canvas?.getContext('2d');

                  canvas.width = imageObj.width;
                  canvas.height = imageObj.height;

                  if (context && canvas.width && canvas.height) {
                     const { storeValues } = getState();
                     let latitude = storeValues?.appData?.address?.latitude;
                     let longitude = storeValues?.appData?.address?.longitude;
                     const latLong =
                        !!latitude && !!longitude ? `${latitude}, ${longitude} ` : '';
                    
                     let caFNumber = storeValues?.appData?.kyc?.cafNumber;
                     const line2Text = `${latLong}${caFNumber ? ' ' + caFNumber : ''}`;
                     const textXPosition = 10;
                     const result = await fetch(faviconIcon);
                     const blob = await result.blob();
                     const image = await createImageBitmap(blob);


                     context.clearRect(0, 0, canvas.width, canvas.height);
                     context.drawImage(
                        imageObj,
                        0,
                        0,
                        imageObj.width,
                        imageObj.height,
                        0,
                        0,
                        imageObj.width,
                        imageObj.height
                     );
                     context.fillStyle = 'white';
                     context.font = 'regular 25px Arial';
                     context.fillText(
                        new Date().toLocaleString(),
                        textXPosition,
                        canvas.height - 35
                     );
                     context.fillText(line2Text, textXPosition, canvas.height - 20);
                     context.translate(textXPosition, canvas.height - 80);
                     context.drawImage(image, 0, 0, 20, 20);
                     context.fill();

                     resolve(canvas.toDataURL('image/jpeg', 1.0));
                  }
               };

               imageObj.src = e?.target?.result;
            }
         } catch (error) {
            reject(error);
         }
      };

      reader.readAsDataURL(file);
   });
};

export const deleteRevampDisabilityProof = ({ args, getState, onUserAction }: any) => {
   try {
      const side = args?.[0];
      if (side !== 'front' && side !== 'back') {
         console.error('Invalid disability delete side', side);
         return;
      }
      const isFront = side === 'front';
      const widgetId = isFront ? 'Disability_UploadFront' : 'Disability_UploadBack';
      const imageUploadedKey = isFront ? 'isFrontImageUploaded' : 'isBackImageUploaded';
      const fileNameKey = isFront ? 'frontFileName' : 'backFileName';
      const { storeValues = {} } = getState();
      const currentPwdData = storeValues?.pwdData ?? {};
      const updatedPwdData = {
         ...currentPwdData,
         [imageUploadedKey]: false,
         [fileNameKey]: '',
      };
      const { isFormComplete, isCtaEnabled } = getDisabilityCtaState({
         ...storeValues,
         pwdData: updatedPwdData,
      });

      updatedPwdData.isFormComplete = isFormComplete;

      onUserAction?.({
         id: `delete-disability-${side}`,
         actions: [
            getStoreValuesAction('pwdData', updatedPwdData),

            getDisabilityCtaAction(isCtaEnabled),

            getNativeEventEmitterAction(widgetId, {
               uploadedFiles: [],
               hideOnUpload: false,
            }),
         ],
      });
   } catch (error) {
      console.error(
         'Error deleting disability proof',

         error
      );
   }
};

const getRevampProcessedImage = ({ getState }: any, file: File): Promise<string> => {
   return new Promise<string>((resolve, reject) => {
      if (!file) {
         reject(new Error('No file selected'));
         return;
      }

      const reader = new FileReader();

      reader.onerror = () => {
         reject(new Error('Failed to read selected file'));
      };

      reader.onload = (e) => {
         const result = e?.target?.result;

         if (typeof result !== 'string') {
            reject(new Error('Invalid file data'));
            return;
         }

         // PDF cannot be processed using an image canvas.
         if (file.type === 'application/pdf') {
            resolve(result);
            return;
         }

         if (!file.type.startsWith('image/')) {
            reject(new Error('Unsupported file type'));
            return;
         }

         const imageObj = new Image();

         imageObj.onerror = () => {
            reject(new Error('Failed to load image'));
         };

         imageObj.onload = async () => {
            try {
               const canvas = document.createElement('canvas');
               const context = canvas.getContext('2d');

               canvas.width = imageObj.naturalWidth;
               canvas.height = imageObj.naturalHeight;

               if (!context || !canvas.width || !canvas.height) {
                  reject(new Error('Unable to process image'));
                  return;
               }

               const { storeValues } = getState();

               let latitude = storeValues?.appData?.address?.latitude;
               let longitude = storeValues?.appData?.address?.longitude;

               const latLong =
                  latitude !== '' && longitude !== '' ? `${latitude}, ${longitude}` : '';


               let caFNumber = storeValues?.appData?.kyc?.cafNumber ?? '';


               const line2Text = [latLong, caFNumber].filter(Boolean).join(' ');

               const textXPosition = 10;

               // Draw original image.
               context.clearRect(0, 0, canvas.width, canvas.height);

               context.drawImage(
                  imageObj,
                  0,
                  0,
                  imageObj.width,
                  imageObj.height,
                  0,
                  0,
                  canvas.width,
                  canvas.height
               );

               context.fillStyle = 'white';
               context.font = '25px Arial';

               context.fillText(
                  new Date().toLocaleString(),
                  textXPosition,
                  canvas.height - 35
               );

               if (line2Text) {
                  context.fillText(line2Text, textXPosition, canvas.height - 20);
               }

               context.translate(textXPosition, canvas.height - 80);

               context.fill();

               resolve(canvas.toDataURL('image/jpeg', 1.0));
            } catch (error) {
               reject(error);
            }
         };

         imageObj.src = result;
      };

      reader.readAsDataURL(file);
   });
};